import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { Stethoscope, Mail, Phone, Lock, Eye, EyeOff, ArrowLeft, ShieldCheck } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", phone: "", new_password: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) { toast.error("Passwords do not match"); return; }
    if (form.new_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", {
        email: form.email.trim(),
        phone: form.phone.trim(),
        new_password: form.new_password,
      });
      setDone(true);
      toast.success("Password reset successfully");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-brand-bg bg-grain px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-lg border border-brand-border p-8 shadow-tactile">
        <Link to="/login" className="text-sm text-brand-muted inline-flex items-center gap-1 hover:text-brand-primary" data-testid="link-back-login">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
        <div className="flex items-center gap-2.5 mt-4 mb-2">
          <img src="/img/acn-logo.png" alt="ACN" className="h-12 w-12 rounded-full object-cover bg-white border border-[#3B2A8C]/20" />
          <div className="leading-tight">
            <div className="font-heading font-bold text-[#3B2A8C] uppercase tracking-wide">Akal College of Nursing</div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-brand-muted">Baru Sahib</div>
          </div>
        </div>
        <h2 className="font-heading text-3xl font-semibold">Forgot password</h2>
        <p className="text-brand-muted mt-2 text-sm">Verify your registered phone to set a new password.</p>

        {done ? (
          <div className="mt-6">
            <div className="rounded-md border border-brand-primary/30 bg-brand-primary/5 p-4 text-sm text-brand-text">
              <div className="inline-flex items-center gap-2 font-heading font-semibold text-brand-primary">
                <ShieldCheck className="h-4 w-4" /> Password updated
              </div>
              <p className="mt-2 text-brand-muted">You can now sign in with your new password.</p>
            </div>
            <button
              onClick={() => navigate("/login")}
              className="mt-4 w-full h-12 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium"
              data-testid="fp-go-login"
            >
              Sign in
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="forgot-form">
            <IconField icon={Mail} label="Email" testId="fp-email">
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="pl-10 input" data-testid="fp-email-input" />
            </IconField>
            <IconField icon={Phone} label="Registered Phone Number" testId="fp-phone">
              <input type="tel" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="pl-10 input" placeholder="10-digit phone" data-testid="fp-phone-input" />
            </IconField>
            <IconField icon={Lock} label="New Password" testId="fp-pw">
              <input type={showPw ? "text" : "password"} required minLength={6} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className="pl-10 pr-12 input" data-testid="fp-pw-input" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 grid place-items-center rounded hover:bg-brand-border/50">
                {showPw ? <EyeOff className="h-4 w-4 text-brand-muted" /> : <Eye className="h-4 w-4 text-brand-muted" />}
              </button>
            </IconField>
            <IconField icon={Lock} label="Confirm New Password" testId="fp-confirm">
              <input type={showPw ? "text" : "password"} required minLength={6} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="pl-10 input" data-testid="fp-confirm-input" />
            </IconField>
            <button type="submit" disabled={loading} className="w-full h-12 rounded-md bg-brand-primary hover:bg-brand-primaryHover text-white font-medium disabled:opacity-60" data-testid="fp-submit">
              {loading ? "Verifying…" : "Reset password"}
            </button>
          </form>
        )}
      </div>
      <style>{`.input { width: 100%; height: 3rem; border-radius: 0.375rem; background: #F2F0EB; border: 1px solid #E5E3DB; outline: none; transition: all 0.2s; }
      .input:focus { background: #fff; box-shadow: 0 0 0 2px #2B5F4E; }`}</style>
    </div>
  );
}

function IconField({ icon: Icon, label, children, testId }) {
  return (
    <div data-testid={testId}>
      <label className="text-xs font-semibold uppercase tracking-wider text-brand-muted">{label}</label>
      <div className="relative mt-1.5">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted z-10" />
        {children}
      </div>
    </div>
  );
}
